import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';
import { useOutletContext } from 'react-router';
import Tabs from '../../../components/Tabs';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useLanguage } from '../../../contexts/language';
import { NewVessel, Vessel } from '../../../types/vessel';
import {
  DAYJS_DATA_DATE_FORMAT,
  DAYJS_VISUALIZATION_DATE_FORMAT,
} from '../../../utils/date';
import { validateNumber } from '../../../utils/validation';
import VesselBasicDetailsForm from './Forms/VesselBasicDetailsForm';
import VesselTechnicalDetailsForm from './Forms/VesselTechnicalDetailsForm';
import { VesselFormValues, VesselTemplateContext } from './types';
import { CommonMainPanelForm } from '../../../components/AdminTemplates';
import useIotsAdminForm from '../../../hooks/useIotsAdminForm';

export interface VesselFormProps {
  onSubmit: (values: NewVessel, callback: () => void) => void;
  onCancel?: () => void;
  isLoading: boolean;
  vessel: Vessel | null;
  disabled?: boolean;
}

const VesselForm = ({
  onSubmit,
  onCancel,
  isLoading,
  disabled = false,
  vessel = null,
}: VesselFormProps) => {
  const { t } = useLanguage();
  const { isOperator, userOperator } = useCurrentUser();
  const { vesselId } = useOutletContext<VesselTemplateContext>();

  const today = dayjs();

  const areInputsDisabled = disabled || isLoading || (isOperator && !!vesselId);

  const initialValues = useMemo<VesselFormValues>(() => {
    if (!vessel) {
      return {
        operatorId: userOperator?.operatorId ?? null,
        name: '',
        flag: '',
        regNumber: '',
        yearOfBuild: '',
        lengthOverall: '',
        beam: '',
        loadDraft: '',
        horsePower: '',
        ratedEngineSpeed: '',
        fuelType: '',
        bunkerCapacity: '',
        averageFuelConsumption: '',
        manningScale: '',
        detailsOfConversion: '',
        currentSurveyStatus: '',
        dryDockingSchedule: '',
        dateOfLastDryDocking: null,
        detailsOfCargoGear: '',
        cargoCapacity: '',
        vesselOwner: '',
        detailsOfDetentions: '',
        passengerCarriageCapacity: '',
        averageCrossingTime: '',
      };
    }
    return {
      operatorId: vessel.operatorId,
      name: vessel.name,
      flag: vessel.flag,
      regNumber: vessel.regNumber,
      yearOfBuild: vessel.yearOfBuild.toString(),
      lengthOverall: vessel.lengthOverall?.toString() ?? '',
      beam: vessel.beam.toString(),
      loadDraft: vessel.loadDraft?.toString() ?? '',
      horsePower: vessel.horsePower.toString(),
      ratedEngineSpeed: vessel.ratedEngineSpeed.toString(),
      fuelType: vessel.fuelType,
      bunkerCapacity: vessel.bunkerCapacity?.toString() ?? '',
      averageFuelConsumption: vessel.averageFuelConsumption.toString(),
      manningScale: vessel.manningScale ?? '',
      detailsOfConversion: vessel.detailsOfConversion ?? '',
      currentSurveyStatus: vessel.currentSurveyStatus ?? '',
      dryDockingSchedule: vessel.dryDockingSchedule,
      dateOfLastDryDocking: dayjs(vessel.dateOfLastDryDocking),
      detailsOfCargoGear: vessel.detailsOfCargoGear ?? '',
      cargoCapacity: vessel.cargoCapacity ?? '',
      vesselOwner: vessel.vesselOwner ?? '',
      detailsOfDetentions: vessel.detailsOfDetentions ?? '',
      passengerCarriageCapacity: vessel.passengerCarriageCapacity ?? '',
      averageCrossingTime: vessel.averageCrossingTime.toString(),
    };
  }, [userOperator, vessel]);

  const formManager = useIotsAdminForm(
    {
      isEditingByDefault: !vessel,
      initialValues,
      onEditionCancel: onCancel,
      onSubmit: (values, callback) => {
        onSubmit(
          {
            operatorId: values.operatorId as string,
            name: values.name.trim(),
            flag: values.flag,
            regNumber: values.regNumber.trim(),
            yearOfBuild: Number(values.yearOfBuild.trim()),
            lengthOverall: values.lengthOverall.trim()
              ? Number(values.lengthOverall.trim())
              : null,
            beam: Number(values.beam.trim()),
            loadDraft: values.loadDraft.trim()
              ? Number(values.loadDraft.trim())
              : null,
            horsePower: Number(values.horsePower.trim()),
            ratedEngineSpeed: Number(values.ratedEngineSpeed.trim()),
            fuelType: values.fuelType as string,
            bunkerCapacity: values.bunkerCapacity.trim()
              ? Number(values.bunkerCapacity.trim())
              : null,
            averageFuelConsumption: Number(
              values.averageFuelConsumption.trim(),
            ),
            manningScale: values.manningScale.trim(),
            detailsOfConversion: values.detailsOfConversion.trim(),
            currentSurveyStatus: values.currentSurveyStatus.trim() || null,
            dryDockingSchedule: values.dryDockingSchedule.trim(),
            dateOfLastDryDocking: (values.dateOfLastDryDocking as Dayjs).format(
              DAYJS_DATA_DATE_FORMAT,
            ),
            detailsOfCargoGear: values.detailsOfCargoGear.trim() || null,
            cargoCapacity: values.cargoCapacity.trim() || null,
            vesselOwner: values.vesselOwner.trim() || null,
            detailsOfDetentions: values.detailsOfDetentions.trim() || null,
            passengerCarriageCapacity:
              values.passengerCarriageCapacity.trim() || null,
            averageCrossingTime: Number(values.averageCrossingTime.trim()),
          },
          callback,
        );
      },
      validate: (values, setError) => {
        const validateNonNegativeNumber = (
          key: keyof VesselFormValues,
          value: string,
          required = false,
        ) => {
          const result = validateNumber(value);
          if (!result.isNumber) {
            if (result.isEmpty && required) {
              setError(key, t('validation.required-field'));
            } else if (!result.isEmpty) {
              setError(key, t('validation.must-be-numeric'));
            }
          } else {
            if (result.isNegative) {
              setError(
                key,
                t(
                  `validation.must-be-number-greater-than-or-equal-to-reference`,
                  { REFERENCE: '0' },
                ),
              );
            }
          }
          return result;
        };

        if (!values.operatorId) {
          setError('operatorId', t('validation.invalid-option'));
        }
        if (!values.name.trim()) {
          setError('name', t('validation.required-field'));
        }
        if (!values.flag) {
          setError('flag', t('validation.invalid-option'));
        }
        if (!values.regNumber.trim()) {
          setError('regNumber', t('validation.required-field'));
        }

        const yearValidation = validateNonNegativeNumber(
          'yearOfBuild',
          values.yearOfBuild,
          true,
        );

        if (yearValidation.isNumber) {
          if (!yearValidation.isInteger) {
            setError('yearOfBuild', t('validation.must-be-numeric-integer'));
          } else if (yearValidation.numericValue > new Date().getFullYear()) {
            setError('yearOfBuild', t('validation.forbidden-future-year'));
          } else if (Number(values.yearOfBuild) <= 1900) {
            setError(
              'yearOfBuild',
              t(`validation.must-be-number-greater-than-reference`, {
                REFERENCE: '1900',
              }),
            );
          }
        }

        validateNonNegativeNumber('beam', values.beam, true);
        validateNonNegativeNumber('lengthOverall', values.lengthOverall);
        validateNonNegativeNumber('horsePower', values.horsePower, true);
        validateNonNegativeNumber(
          'ratedEngineSpeed',
          values.ratedEngineSpeed,
          true,
        );
        validateNonNegativeNumber('loadDraft', values.loadDraft);
        validateNonNegativeNumber('bunkerCapacity', values.bunkerCapacity);

        if (!values.fuelType) {
          setError('fuelType', t('validation.invalid-option'));
        }

        validateNonNegativeNumber(
          'averageFuelConsumption',
          values.averageFuelConsumption,
          true,
        );

        if (!values.manningScale.trim()) {
          setError('manningScale', t('validation.required-field'));
        }

        if (!values.currentSurveyStatus) {
          setError('currentSurveyStatus', t('validation.required-field'));
        }

        if (!values.dryDockingSchedule.trim()) {
          setError('dryDockingSchedule', t('validation.required-field'));
        }

        if (!values.dateOfLastDryDocking) {
          setError('dateOfLastDryDocking', t('validation.required-field'));
        } else if (!values.dateOfLastDryDocking.isValid()) {
          setError('dateOfLastDryDocking', t('validation.invalid-date'));
        } else if (values.dateOfLastDryDocking.isAfter(today, 'day')) {
          setError(
            'dateOfLastDryDocking',
            t('validation.date-after-reference-date', {
              REFERENCE_DATE: today.format(DAYJS_VISUALIZATION_DATE_FORMAT),
            }),
          );
        }

        if (!values.vesselOwner) {
          setError('vesselOwner', t('validation.required-field'));
        }
        if (!values.detailsOfDetentions) {
          setError('detailsOfDetentions', t('validation.required-field'));
        }
        if (!values.passengerCarriageCapacity) {
          setError('passengerCarriageCapacity', t('validation.required-field'));
        }

        const averageCrossingTimeValidation = validateNonNegativeNumber(
          'averageCrossingTime',
          values.averageCrossingTime,
          true,
        );
        if (
          averageCrossingTimeValidation.isNumber &&
          !averageCrossingTimeValidation.isInteger
        ) {
          setError(
            'averageCrossingTime',
            t('validation.must-be-numeric-integer'),
          );
        }

        if (!values.detailsOfConversion) {
          setError('detailsOfConversion', t('validation.required-field'));
        }
      },
      resetOnInitialValuesChange: () =>
        Boolean(vessel || userOperator?.operatorId),
    },
    { shouldInputsBeDisabled: areInputsDisabled },
  );

  const {
    formState: {
      errors,
      handleSubmit,
      hasVisibleErrors,
      haveValuesChanged,
      cancelEdition,
      requestEdition,
      isEditing,
    },
  } = formManager;

  const isForm1Error = Boolean(
    errors.operatorId ||
      errors.name ||
      errors.flag ||
      errors.regNumber ||
      errors.yearOfBuild,
  );
  const isForm2Error = Boolean(
    errors.beam ||
      errors.horsePower ||
      errors.ratedEngineSpeed ||
      errors.fuelType ||
      errors.averageCrossingTime ||
      errors.manningScale ||
      errors.detailsOfConversion ||
      errors.currentSurveyStatus ||
      errors.dryDockingSchedule ||
      errors.dateOfLastDryDocking ||
      errors.vesselOwner ||
      errors.detailsOfDetentions ||
      errors.passengerCarriageCapacity ||
      errors.averageCrossingTime,
  );

  return (
    <CommonMainPanelForm
      onSubmit={handleSubmit}
      onCancelEdition={cancelEdition}
      onEditionRequest={requestEdition}
      isEditing={isEditing}
      isSubmitDisabled={
        areInputsDisabled || !haveValuesChanged || hasVisibleErrors
      }
      isSubmitting={isLoading}
      isEditDisabled={areInputsDisabled}
    >
      <div>
        <Tabs
          tabs={[
            {
              label: t('content.basic-details'),
              icon: isForm1Error ? 'error' : undefined,
            },
            {
              label: t('content.technical-details'),
              icon: isForm2Error ? 'error' : undefined,
            },
          ]}
        >
          <VesselBasicDetailsForm formManager={formManager} />
          <VesselTechnicalDetailsForm formManager={formManager} />
        </Tabs>
      </div>
    </CommonMainPanelForm>
  );
};

export default VesselForm;
